import "../Components/ChartContainer/ChartContainer.scss";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as R from "ramda";
import { KnowYourCustomerData } from "@blisspointmedia/bpm-types/dist/KnowYourCustomer";
import WidgetContainer from "../Components/WidgetContainer";
import { CBSA_TO_MSA_MAP, CbsaMap } from "../Components/CbsaMap";
import { ConvergentColorMap6, DivergentColorMap8 } from "../utils/colors";
import { formatDecimal, formatPercent } from "../utils/format-utils";
import { TabKey, ViewOptions } from "./customerInsightsConstants";
import ChartContainer from "../Components/ChartContainer";
import {
  Button,
  ButtonFrameworkVariant,
  ButtonType,
  InfoTooltip,
  InfoTooltipDirection,
} from "../Components";
import { MdOutlineFileDownload } from "react-icons/md";
import { downloadJSONToCSV } from "../utils/download-utils";
import { useSetError } from "../redux/modals";
import CustomerInsightsTable from "./CustomerInsightsTable";
import {
  getColorForIndexedVolume,
  getThresholdBinsWithStdDev,
  getThresholdBinsWithStdDevNonAdjusted,
} from "../BrandHealthMetrics/BrandHealthMetricsUtils";
import GradientLegend from "../BrandHealthMetrics/GradientLegend";
import { useCompanyInfo } from "../redux/company";

interface CustomerInsightsGeoGroupProps {
  data: KnowYourCustomerData[];
  totalPopulation: number;
  header: string;
  tab: TabKey;
  graphView: ViewOptions;
}

const CustomerInsightsGeoGroup: React.FC<CustomerInsightsGeoGroupProps> = ({
  data,
  totalPopulation,
  header,
  tab,
  graphView,
}) => {
  const setError = useSetError();
  const { cid, name } = useCompanyInfo();
  const [companyName, setCompanyName] = useState<string>("Tinuiti");

  useEffect(() => {
    if (cid !== "tinuititest") {
      setCompanyName(name);
    }
  }, [cid, name]);

  const colorMap = useMemo(
    () => (tab === TabKey.INDEX ? DivergentColorMap8 : ConvergentColorMap6),
    [tab]
  );

  const knownPopulation = useMemo(() => {
    const unknownPop = data.find(row => row.levels === "Unknown")?.clientPopulation ?? 0;
    return totalPopulation - unknownPop;
  }, [data, totalPopulation]);

  const getMetricFromData = useCallback(
    (data: KnowYourCustomerData, tabInput: TabKey = tab): number => {
      switch (tabInput) {
        case TabKey.CUSTOMER_BASE:
          return data.clientPopulation / knownPopulation;
        case TabKey.INDEX:
          return data.clientPopulationFraction / data.tuPopulationFraction;
        case TabKey.PENETRATION:
          return data.clientPopulation / data.tuPopulation;
      }
    },
    [tab, knownPopulation]
  );

  const mappedData: Record<string, KnowYourCustomerData> = useMemo(
    () => R.indexBy(R.prop("levels"), data),
    [data]
  );

  const formatLabel = useMemo(
    () => data => (tab !== TabKey.INDEX ? formatPercent(data, 2) : formatDecimal(data, 2)),
    [tab]
  );

  const thresholdBins: Record<TabKey, number[]> = useMemo(
    () => ({
      [TabKey.CUSTOMER_BASE]: getThresholdBinsWithStdDevNonAdjusted(
        data.map(val =>
          getMetricFromData(
            mappedData[val.levels] ?? mappedData[CBSA_TO_MSA_MAP[val.levels]],
            TabKey.CUSTOMER_BASE
          )
        )
      ),
      [TabKey.INDEX]: getThresholdBinsWithStdDev(
        data.map(val =>
          getMetricFromData(
            mappedData[val.levels] ?? mappedData[CBSA_TO_MSA_MAP[val.levels]],
            TabKey.INDEX
          )
        )
      ),
      [TabKey.PENETRATION]: getThresholdBinsWithStdDevNonAdjusted(
        data.map(val =>
          getMetricFromData(
            mappedData[val.levels] ?? mappedData[CBSA_TO_MSA_MAP[val.levels]],
            TabKey.PENETRATION
          )
        )
      ),
    }),
    [data, getMetricFromData, mappedData]
  );

  const getRenderColor = useCallback(
    (cbsa: string): string => {
      const metricVal = getMetricFromData(mappedData[cbsa] ?? mappedData[CBSA_TO_MSA_MAP[cbsa]]);
      if (!metricVal) {
        console.error(`No value found for cbsa ${cbsa} / mapped msa ${CBSA_TO_MSA_MAP[cbsa]}`);
        return "transparent";
      }

      return getColorForIndexedVolume(metricVal, thresholdBins[tab], colorMap);
    },
    [colorMap, getMetricFromData, mappedData, tab, thresholdBins]
  );

  const explanationText = useMemo(() => {
    switch (tab) {
      case TabKey.CUSTOMER_BASE:
        return "((Number of customers) /\n(ingested emails)) per MSA";
      case TabKey.INDEX:
        return "((percent of our customers) /\n(percent of total population)) per MSA";
      case TabKey.PENETRATION:
        return "((Number of customers) /\n(total population)) per MSA";
    }
  }, [tab]);

  const widgetTitle = useMemo(() => {
    switch (tab) {
      case TabKey.CUSTOMER_BASE:
        return "Customer Population per MSA";
      case TabKey.INDEX:
        return "Customer Index per MSA";
      case TabKey.PENETRATION:
        return "Customer Penetration per MSA";
    }
  }, [tab]);

  const tooltipCopy = useMemo(() => {
    switch (tab) {
      case TabKey.CUSTOMER_BASE:
        return "The percentage of your customer base residing in each MSA.";
      case TabKey.INDEX:
        return "The relative percentage of your customer base residing in each MSA, as compared to the percentage of the total U.S. market.";
      case TabKey.PENETRATION:
        return "The percentage of the total MSA population that is represented in your customer base.";
    }
  }, [tab]);

  const renderTable = useMemo(
    () => (
      <div className="geoTable">
        <CustomerInsightsTable tab={tab} fieldData={data} />
      </div>
    ),
    [data, tab]
  );

  const renderGraph = useMemo(
    () => (
      <div className="geoMap">
        <GradientLegend
          bins={thresholdBins[tab]}
          useConvergent={tab !== TabKey.INDEX}
          formatLabel={formatLabel}
        ></GradientLegend>
        <div className="cbsaMapContainer">
          <CbsaMap
            renderColor={getRenderColor}
            renderTooltip={cbsa => {
              const data = getMetricFromData(mappedData[cbsa] ?? mappedData[CBSA_TO_MSA_MAP[cbsa]]);
              return (
                <div>
                  {cbsa}: {formatLabel(data)}
                </div>
              );
            }}
          />
        </div>
      </div>
    ),
    [formatLabel, getMetricFromData, getRenderColor, mappedData, tab, thresholdBins]
  );

  return (
    <WidgetContainer header={header} collapsible headerCollapsibleOnclick enableHoverDesign={false}>
      <div className="customerInsightsGroup fullWidth">
        <ChartContainer
          title={widgetTitle}
          enableHoverDesign
          rightActions={
            <>
              <InfoTooltip direction={InfoTooltipDirection.BOTTOM_LEFT}>
                {tooltipCopy}
                <p>Calculated as: {explanationText}</p>
              </InfoTooltip>
              <Button
                type={ButtonType.FILLED}
                variant={ButtonFrameworkVariant.ICON_ONLY}
                icon={<MdOutlineFileDownload />}
                onClick={() => {
                  if (!data) {
                    setError({ message: "No data available to download" });
                    return;
                  }

                  const formattedData = data.map(row => ({
                    "MSA Name": row.levels,
                    [`${companyName}'s Population`]: Math.round(row.clientPopulation),
                    [`${companyName}'s Distribution`]: row.clientPopulationFraction,
                    "US Market": Math.round(row.tuPopulation),
                    "US Market Distribution": row.tuPopulationFraction,
                    Index: getMetricFromData(row, TabKey.INDEX),
                    Penetration: getMetricFromData(row, TabKey.PENETRATION),
                  }));
                  const date = new Date();
                  downloadJSONToCSV(
                    formattedData,
                    `CustomerInsights ${companyName} MSAs ${date.toLocaleString()}`
                  );
                }}
              />
            </>
          }
        >
          {graphView === "showGraph" && renderGraph}
          {graphView === "showTable" && renderTable}
        </ChartContainer>
      </div>
    </WidgetContainer>
  );
};

export default CustomerInsightsGeoGroup;
